// src/components/common/PinKeypad.jsx
import React, { useEffect, useCallback, useRef } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Backspace as BackspaceIcon } from '@mui/icons-material';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '', '0', 'backspace'];

function isEditableTarget(target) {
  if (!target) return false;
  const tag = target.tagName;
  return tag === 'INPUT' || tag === 'TEXTAREA' || target.isContentEditable;
}

/**
 * Custom numeric keypad for PIN entry — no backing <input>, so it never pops
 * a mobile/touchscreen keyboard. Works by both tap/click on the on-screen
 * keys AND physical keyboard digit/backspace/enter presses, captured via a
 * document-level keydown listener (skipped while a real text input has
 * focus, so it never steals keystrokes from e.g. a password field on the
 * same dialog). Clinics are roughly 50/50 touchscreen vs keyboard+mouse, so
 * both paths need to work without the user picking one.
 */
export default function PinKeypad({
  value,
  onChange,
  length = 4,
  disabled = false,
  autoCapture = true,
  error = false,
  onComplete,
  onEnter,
}) {
  const firedCompleteFor = useRef('');

  const append = useCallback((digit) => {
    if (disabled || value.length >= length) return;
    onChange(value + digit);
  }, [value, onChange, length, disabled]);

  const backspace = useCallback(() => {
    if (disabled) return;
    onChange(value.slice(0, -1));
  }, [value, onChange, disabled]);

  useEffect(() => {
    if (!autoCapture) return undefined;

    const handleKeyDown = (event) => {
      if (isEditableTarget(event.target)) return;

      if (event.key >= '0' && event.key <= '9') {
        event.preventDefault();
        append(event.key);
      } else if (event.key === 'Backspace') {
        event.preventDefault();
        backspace();
      } else if (event.key === 'Enter' && onEnter) {
        event.preventDefault();
        onEnter();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [autoCapture, append, backspace, onEnter]);

  useEffect(() => {
    if (value.length === length && onComplete && firedCompleteFor.current !== value) {
      firedCompleteFor.current = value;
      onComplete(value);
    } else if (value.length < length) {
      firedCompleteFor.current = '';
    }
  }, [value, length, onComplete]);

  return (
    <Box>
      <Box display="flex" justifyContent="center" gap={2} mb={3}>
        {Array.from({ length }).map((_, i) => (
          <Box
            key={i}
            sx={{
              width: 18,
              height: 18,
              borderRadius: '50%',
              border: '2px solid',
              borderColor: error ? 'error.main' : 'primary.main',
              backgroundColor: i < value.length ? (error ? 'error.main' : 'primary.main') : 'transparent',
              transition: 'background-color 0.15s',
            }}
          />
        ))}
      </Box>

      <Box
        display="grid"
        gridTemplateColumns="repeat(3, 1fr)"
        gap={1.5}
        sx={{ maxWidth: 280, mx: 'auto' }}
      >
        {KEYS.map((key, i) => {
          if (key === '') return <Box key={i} />;

          if (key === 'backspace') {
            return (
              <ButtonBase
                key={i}
                onClick={backspace}
                disabled={disabled || value.length === 0}
                sx={keyStyle}
              >
                <BackspaceIcon />
              </ButtonBase>
            );
          }

          return (
            <ButtonBase
              key={i}
              onClick={() => append(key)}
              disabled={disabled || value.length >= length}
              sx={keyStyle}
            >
              <Typography variant="h5" fontWeight={500}>{key}</Typography>
            </ButtonBase>
          );
        })}
      </Box>
    </Box>
  );
}

const keyStyle = {
  height: 64,
  borderRadius: 2,
  bgcolor: 'action.hover',
  transition: 'background-color 0.1s',
  '&:hover': { bgcolor: 'action.selected' },
  '&:active': { bgcolor: 'action.selected' },
  '&.Mui-disabled': { opacity: 0.4 },
};
